import * as preact from "preact";
import { $$, DateFormat } from "../../Core/Common";
import { BaseComponent, IBaseComponentProps } from "../BaseComponent/BaseComponent";
import { DataType } from "../SharedEnums/SharedEnums";


export interface IColProps<RecordType>
{
    width?: string;
    header?: string;
    dataFormatter?: DataFormatter;
    dataType?: DataType;
    dataKey?: keyof RecordType;
    dataCallback?: (RecordType) => string | number;
    sortIndex?: number;
}

interface IAdvancedTableProps<RecordType> extends IBaseComponentProps
{
    cols: IColProps<RecordType>[];
    onRowClick?: (id) => void;
    onSort?: (sortIndex: number) => void;
    onPageChange?: (pageNumber: number) => void;
    enablePagination?: boolean;
}

export enum DataFormatter
{
    ShortDateTime = 1,
    ShortDate = 2,
    Html = 3,
    YesNo = 4
}

export interface IRecordBase
{
    Id?: string | number;
    Context?: string;
    Name?: string;
}

export class AdvancedTable<RecordType extends IRecordBase> extends BaseComponent<IAdvancedTableProps<RecordType>>
{
    public data: RecordType[] = [];
    private _body: HTMLTableSectionElement;
    private _selectedId: string;
    private _selectedRow: HTMLElement;
    private _sortKey: number = 1;

    constructor(props: IAdvancedTableProps<RecordType>)
    {
        super(props);
    }

    private _formatColumnData(column: IColProps<RecordType>, rawValue): string
    {
        if (rawValue == null)
        {
            return "";
        }

        switch (column.dataFormatter)
        {
            case DataFormatter.YesNo:
                return rawValue ? "Yes" : "No";
                break;
            case DataFormatter.ShortDate:
                if (rawValue instanceof Date)
                {
                    return $$.formatDate(rawValue, DateFormat.ShortDate);
                }
                return $$.formatFlexDate(rawValue);
            case DataFormatter.ShortDateTime:
                return rawValue.localeFormat("d") + " " + rawValue.localeFormat("t");

            default:
                return rawValue.toString();
        }
    }

    private _onTableClick(event: MouseEvent)
    {
        if (this.props.onRowClick)
        {
            let target = event.target;
            let headCell = $$.getParent(target as HTMLElement, "th");
            if (headCell)
            {
                let sortIndex = parseInt(headCell.dataset["sortIndex"]);
                if (!sortIndex)
                {
                    return;
                }

                if (Math.abs(this._sortKey) === sortIndex)
                {
                    this._sortKey = this._sortKey * -1;
                }
                else
                {
                    this._sortKey = sortIndex;
                }

                if (this.props.onSort)
                {
                    this.props.onSort(this._sortKey);
                }

                return;
            }

            let row = $$.getParent(target as HTMLElement, "tr");

            if (row)
            {
                var id = row.dataset.id;
                if (id)
                {
                    if (this._selectedRow)
                    {
                        this._selectedRow.classList.remove("selected");
                    }

                    row.className = "selected";
                    this._selectedId = id;
                    this._selectedRow = row;

                    if (this.props.onRowClick)
                    {
                        this.props.onRowClick(id);
                    }
                }
            }
        }
    }

    public override componentDidMount()
    {
        super.componentDidMount();
    }

    public setData(data: RecordType[], keepRow?: boolean)
    {
        if (this._selectedRow)
        {
            this._selectedRow.classList.remove("selected");
            this._selectedRow = null;
        }

        if (!keepRow)
        {
            this._selectedId = null;
        }

        this.data = data;
        this._updateRows();
    }

    private _updateRows()
    {
        this._body.innerHTML = "";
        $$.appendChild(this._body, this.data.map((row, index) => this._renderRow(row, index)));
    }

    private _renderRow(row, index)
    {
        return (<tr data-id={row.Id} ref={(ref) => { if (ref.className == "selected") { this._selectedRow = ref; }}} className={(this._selectedId && this._selectedId == row.Id) ? "selected" : ""}>
            {this.props.cols.map((col, colIndex) =>
            {
                let rawValue;
                let sortValue = null;
                if (col.dataCallback)
                {
                    rawValue = col.dataCallback(row);
                }
                else
                {
                    rawValue = row[col.dataKey];
                    if (rawValue?.DBDate)
                    {
                        sortValue = rawValue.DBDate;
                    }
                }

                return col.dataFormatter === DataFormatter.Html ?
                    (<td data-text={sortValue ? sortValue : null} dangerouslySetInnerHTML={{ __html: rawValue ?? "" }} />) :
                    (<td data-text={sortValue ? sortValue : null}>{this._formatColumnData(col, rawValue)}</td>)
            })}
        </tr>);
    }

    public render()
    {
        return (
            <table className="summaryList" ref={(ref) => this._registerContainer(ref)} onClick={this._onTableClick.bind(this)}>
                <colgroup>
                    {this.props.cols.map((col, index) => (<col width={col.width ?? ""} />))}
                </colgroup>
                <thead>
                    <tr>
                        {
                            this.props.cols.map((col, index) => (<th data-sort-index={col.sortIndex ?? ""} style={{ cursor: col.sortIndex ? "pointer" : "" }}>{col.header}</th>))
                        }
                    </tr>
                </thead>
                <tbody ref={(ref) => this._body = ref} >{this.data.map((row, index) => this._renderRow(row, index))}</tbody>
            </table>);
    }
}